import { useEffect, useRef } from "react";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { apiRequest } from "@/lib/queryClient";

export type BankFeedAccount = {
  id: number;
  institutionName: string;
  displayName: string;
  last4: string | null;
  status: string;
  lastSyncedAt: string | null;
  syncError: string | null;
  propertyId: number | null;
  livemode: boolean;
};
export type BankFeedsResponse = {
  mode: "test" | "live";
  available: boolean;
  accounts: BankFeedAccount[];
  message?: string;
};
export const BANK_FEEDS_KEY = ["/api/app/bank-feeds"] as const;
export function bankFeedsPollingInterval(data?: BankFeedsResponse): number | false {
  return data?.accounts.some((account) => account.status === "syncing" || account.status === "pending") ? 3000 : false;
}

export function useBankFeeds({ enabled, canEdit }: { enabled: boolean; canEdit: boolean }) {
  const queryClient = useQueryClient();
  const refreshFinancials = () => Promise.all(
    ["/api/app/income", "/api/app/expenses", "/api/app/profit-and-loss", "/api/app/financials", "/api/app/balance-sheet"]
      .map((key) => queryClient.invalidateQueries({ queryKey: [key] })),
  );
  const refresh = () => Promise.all([
    queryClient.invalidateQueries({ queryKey: BANK_FEEDS_KEY }),
    refreshFinancials(),
  ]);
  const query = useQuery<BankFeedsResponse>({
    queryKey: BANK_FEEDS_KEY,
    queryFn: async () => (await apiRequest("GET", BANK_FEEDS_KEY[0])).json(),
    enabled,
    retry: false,
    refetchInterval: (q) => bankFeedsPollingInterval(q.state.data),
  });
  // Background imports can finish after the sync POST returns. Refresh the
  // ledgers when a poll reports a changed status or sync timestamp.
  const signature = query.data?.accounts.map((a) => `${a.id}:${a.status}:${a.lastSyncedAt}`).join("|");
  const previousSignature = useRef<string | undefined>(undefined);
  useEffect(() => {
    if (enabled && signature !== undefined) {
      if (previousSignature.current !== undefined && previousSignature.current !== signature) {
        void Promise.all(["/api/app/income", "/api/app/expenses", "/api/app/profit-and-loss", "/api/app/financials"]
          .map((key) => queryClient.invalidateQueries({ queryKey: [key] })));
      }
      previousSignature.current = signature;
    }
  }, [enabled, signature, queryClient]);
  const assertEditable = () => {
    if (!enabled || !canEdit) throw new Error("You need Financials edit permission to manage bank feeds.");
  };
  const connect = useMutation({
    mutationFn: async () => {
      assertEditable();
      const session: { sessionId: string; clientSecret: string; publishableKey: string } =
        await (await apiRequest("POST", "/api/app/bank-feeds/sessions", {})).json();
      // Stripe.js is loaded only when the user starts a connection.
      const { loadStripe } = await import("@stripe/stripe-js");
      const stripe = await loadStripe(session.publishableKey);
      if (!stripe) throw new Error("Stripe could not load. Check your connection and try again.");
      const result = await stripe.collectFinancialConnectionsAccounts({ clientSecret: session.clientSecret });
      if (result.error) throw new Error(result.error.message || "Bank connection was not completed. You can try again.");
      if (!result.financialConnectionsSession) throw new Error("Bank connection was cancelled. No accounts were connected.");
      // Never trust account IDs returned by the browser: the server retrieves
      // this session's accounts directly from Stripe.
      await apiRequest("POST", `/api/app/bank-feeds/sessions/${encodeURIComponent(session.sessionId)}/complete`, {});
    },
    onSuccess: refresh,
  });
  const sync = useMutation({
    meta: { silentSave: true },
    mutationFn: async (id: number): Promise<{ imported: number; status: string; message?: string }> => {
      assertEditable();
      return (await apiRequest("POST", `/api/app/bank-feeds/accounts/${id}/sync`, {})).json();
    },
    onSuccess: refresh,
  });
  const disconnect = useMutation({
    mutationFn: async (id: number) => {
      assertEditable();
      await apiRequest("DELETE", `/api/app/bank-feeds/accounts/${id}`);
    },
    onSuccess: refresh,
  });
  const assignProperty = useMutation({
    mutationFn: async ({ id, propertyId }: { id: number; propertyId: number | null }) => {
      assertEditable();
      await apiRequest("PATCH", `/api/app/bank-feeds/accounts/${id}`, { propertyId });
    },
    onSuccess: (_data, { id, propertyId }) => {
      queryClient.setQueryData<BankFeedsResponse>(BANK_FEEDS_KEY, (old) => old ? {
        ...old, accounts: old.accounts.map((a) => a.id === id ? { ...a, propertyId } : a),
      } : old);
    },
  });
  return { query, connect, sync, disconnect, assignProperty };
}
